import { getChartLabelIdOrName } from '@modules/charts/utils';
import { GetChartLabelOptions } from '@modules/common/types';
import {
  useGetCompareChartData,
  useGetQuantifyChartData,
  useGetQuantifyCompareQuery,
  useIsAnomaliesTrendVisible,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { QuantifyReportDataPointBase } from '@modules/quantify/store/types';
import { Box, Stack, Typography, TypographyProps } from '@mui/material';
import pluralize from 'pluralize';

interface QuantifyOrCompareChartTitleProps extends TypographyProps {
  data: QuantifyReportDataPointBase[];
  getLabelOptions?: GetChartLabelOptions;
}

export function QuantifyOrCompareChartTitle({
  data,
  getLabelOptions,
  ...rest
}: QuantifyOrCompareChartTitleProps) {
  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const isAnomaliesTrendVisible = useIsAnomaliesTrendVisible();
  const compareQuery = useGetQuantifyCompareQuery();
  const quantifyChartData = useGetQuantifyChartData();
  const compareChartData = useGetCompareChartData();
  const chartDataPoints = compareQuery ? compareChartData : quantifyChartData;

  const selectedCount = (() => {
    if (!chartDataPoints) {
      return 0;
    }

    const chartDataPointsSet = new Set<string>();
    chartDataPoints.forEach((chartPoint) => {
      const { id, label } = chartPoint;
      if (!id || !label) {
        return;
      }
      chartDataPointsSet.add(id);
    });

    return chartDataPointsSet.size;
  })();

  if (selectedCount <= 1) {
    const firstSelectedDataPoint = data.find((dataPoint) =>
      Boolean(dataPoint.label),
    );
    const shouldShowCategory = !!firstSelectedDataPoint;

    const displayName = getChartLabelIdOrName(firstSelectedDataPoint?.label);

    if (shouldShowCategory) {
      return (
        <Stack direction="row" spacing={0.5} alignItems="baseline">
          <Typography variant="subtitle2" {...rest}>
            Showing:
          </Typography>
          <Box>
            {getLabelOptions?.({ id: firstSelectedDataPoint.id })?.startIcon}
          </Box>
          <Typography variant="subtitle2">{displayName}</Typography>
        </Stack>
      );
    }

    return (
      <Typography variant="subtitle2" {...rest}>
        Showing: {displayName}
      </Typography>
    );
  }

  const groupByLabel = !!groupBy?.detail?.metadata
    ? groupBy.displayName
    : pluralize(groupBy?.displayName || 'record');

  return (
    <Typography variant="subtitle2" {...rest} data-testid="chart-title">
      Showing {selectedCount}{' '}
      {isAnomaliesTrendVisible ? 'anomalous' : 'selected'} {groupByLabel}
    </Typography>
  );
}
